import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { FileText } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import BuildingPageHeading from '../../components/BuildingPageHeading';
import SectionCard from '../../components/SectionCard';

function fileApiPath(url) {
  return String(url || '').replace(/^\/api/, '');
}

export default function BuildingComplianceSchedule() {
  const { id } = useParams();
  const [selectedName, setSelectedName] = useState('');
  const [blobUrl, setBlobUrl] = useState('');
  const [loadingFile, setLoadingFile] = useState(false);

  const { data: building } = useQuery({
    queryKey: ['building', id],
    queryFn: async () => (await api.get(`/buildings/${id}`)).data,
    enabled: !!id,
  });

  const { data: pdfs = [], isLoading } = useQuery({
    queryKey: ['building', id, 'compliance-schedules'],
    queryFn: async () => {
      const response = await api.get(`/buildings/${id}/compliance-schedules`);
      return response.data?.data || [];
    },
    enabled: !!id,
  });

  const selected = pdfs.find((pdf) => pdf.filename === selectedName) || pdfs[0] || null;

  useEffect(() => {
    if (!selected?.url) {
      setBlobUrl('');
      return undefined;
    }
    let objectUrl = '';
    let cancelled = false;
    setLoadingFile(true);
    api
      .get(fileApiPath(selected.url), { responseType: 'blob' })
      .then((res) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
        setBlobUrl(objectUrl);
      })
      .catch(() => {
        if (!cancelled) {
          setBlobUrl('');
          toast.error('Could not open the Compliance Schedule copy');
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingFile(false);
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [selected?.url]);

  const number = building?.compliance_schedule_number || '';

  return (
    <div>
      <BuildingPageHeading
        buildingId={id}
        buildingName={building?.name}
        title="Compliance Schedule"
      />

      <SectionCard title="Compliance Schedule copy">
        {isLoading ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : pdfs.length === 0 ? (
          <p className="text-sm text-gray-600">
            No Compliance Schedule copy is stored for this building.
            {number ? ` Number on file: ${number}.` : ''}{' '}
            <Link to={`/buildings/${id}/edit`} className="text-primary-700 underline">
              Upload one on Edit Building
            </Link>
            .
          </p>
        ) : (
          <div className="space-y-3">
            {number ? (
              <p className="text-sm text-gray-700">
                Compliance Schedule <span className="font-semibold text-gray-900">{number}</span>
              </p>
            ) : null}
            {pdfs.length > 1 ? (
              <div className="flex flex-wrap gap-2">
                {pdfs.map((pdf) => {
                  const on = pdf.filename === selected?.filename;
                  return (
                    <button
                      key={pdf.filename}
                      type="button"
                      onClick={() => setSelectedName(pdf.filename)}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border ${
                        on
                          ? 'border-green-400 bg-green-200 text-gray-900'
                          : 'border-green-300 bg-green-100 text-gray-700 hover:bg-green-200'
                      }`}
                    >
                      <FileText className="w-4 h-4" />
                      <span className="truncate max-w-[16rem]">{pdf.originalName || pdf.filename}</span>
                    </button>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-gray-700 inline-flex items-center gap-1.5">
                <FileText className="w-4 h-4" />
                {selected?.originalName || selected?.filename}
              </p>
            )}
            {loadingFile ? <p className="text-sm text-gray-500">Opening copy…</p> : null}
            {blobUrl ? (
              <iframe
                title="Compliance Schedule copy"
                src={blobUrl}
                className="w-full h-[75vh] rounded-lg border border-green-300 bg-white"
              />
            ) : null}
          </div>
        )}
      </SectionCard>
    </div>
  );
}
